import React, { useEffect, useMemo, useState } from 'react';
import { X, RefreshCw, Send, Paperclip, Link2 } from 'lucide-react';
import { api } from '../services/api';

const OutboundSmsComposerModal = ({ open, onClose, contact }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(null);
  const [body, setBody] = useState('');
  const [showDocumentPicker, setShowDocumentPicker] = useState(false);
  const [availableDocuments, setAvailableDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [generatingLink, setGeneratingLink] = useState(null);

  const charCount = useMemo(() => (body || '').length, [body]);
  const isOptedOut = !!contact?.sms_opted_out;

  const loadDraft = async () => {
    if (!contact?.id) return;
    setIsLoading(true);
    setError(null);
    try {
      const draft = await api.draftOutboundSms(contact.id);
      setBody(draft.body || '');
    } catch (e) {
      setError(e?.message || 'Failed to generate draft');
    } finally {
      setIsLoading(false);
    }
  };

  const loadDocuments = async () => {
    setLoadingDocuments(true);
    try {
      const response = await api.getDocuments({ limit: 100 });
      setAvailableDocuments(response.documents || []);
    } catch (e) {
      console.error('Failed to load documents:', e);
    } finally {
      setLoadingDocuments(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadDraft();
      loadDocuments();
    } else {
      setError(null);
      setBody('');
      setShowDocumentPicker(false);
      setIsLoading(false);
      setIsSending(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contact?.id]);

  const handleSend = async () => {
    if (!contact?.id) return;
    setIsSending(true);
    setError(null);
    try {
      await api.sendOutboundSms(contact.id, { body });
      onClose?.({ sent: true });
    } catch (e) {
      setError(e?.message || 'Failed to send SMS');
    } finally {
      setIsSending(false);
    }
  };

  const insertDocumentLink = async (doc) => {
    setGeneratingLink(doc.id);
    try {
      // Generate a short link for the document
      const response = await api.generateDocumentShortLink(doc.id, contact?.id);
      const shortUrl = response.short_url;
      
      // Insert at cursor or append to message
      const linkText = ` ${shortUrl}`;
      setBody(prev => prev + linkText);
      setShowDocumentPicker(false);
    } catch (e) {
      console.error('Failed to generate document link:', e);
      setError('Failed to generate document link');
    } finally {
      setGeneratingLink(null);
    }
  };

  const getFileIcon = (mimeType) => {
    if (mimeType?.includes('pdf')) return '📄';
    if (mimeType?.includes('image')) return '🖼️';
    if (mimeType?.includes('word') || mimeType?.includes('document')) return '📝';
    if (mimeType?.includes('excel') || mimeType?.includes('sheet')) return '📊';
    return '📎';
  };

  if (!open) return null;

  return (
    <div style={styles.overlay} onClick={() => onClose?.({ sent: false })}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <div>
            <div style={styles.title}>Send SMS</div>
            <div style={styles.subtitle}>
              To: <span style={{ color: '#e2e8f0' }}>{contact?.phone || '(no phone)'}</span>
            </div>
          </div>
          <button style={styles.iconBtn} onClick={() => onClose?.({ sent: false })}>
            <X size={18} />
          </button>
        </div>

        {isOptedOut && (
          <div style={styles.warning}>
            SMS send is disabled — this contact has opted out (STOP){contact?.sms_opted_out_at ? ` at ${new Date(contact.sms_opted_out_at).toLocaleString()}` : ''}.
            Reply START from the customer's phone to re-enable.
          </div>
        )}

        {error && (
          <div style={styles.error}>
            {error}
          </div>
        )}

        <div style={styles.form}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <label style={styles.label}>Message</label>
            <div style={{ color: '#64748b', fontSize: 12 }}>{charCount} chars</div>
          </div>
          <textarea
            style={styles.textarea}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Write your SMS..."
            rows={6}
            disabled={isLoading || isSending || isOptedOut}
          />
          <div style={{ color: '#64748b', fontSize: 12, marginTop: 6 }}>
            Keep it short. No emojis or hype. One question max.
          </div>

          {/* Document Link Section */}
          <div style={styles.attachmentsSection}>
            <div style={styles.attachmentsHeader}>
              <label style={styles.label}>Document Links</label>
              <button
                style={styles.attachButton}
                onClick={() => setShowDocumentPicker(!showDocumentPicker)}
                disabled={isLoading || isSending || isOptedOut}
              >
                <Link2 size={16} />
                {showDocumentPicker ? 'Hide Documents' : 'Insert Document Link'}
              </button>
            </div>

            {/* Document Picker */}
            {showDocumentPicker && (
              <div style={styles.documentPicker}>
                {loadingDocuments ? (
                  <div style={styles.loadingDocs}>Loading documents...</div>
                ) : availableDocuments.length === 0 ? (
                  <div style={styles.noDocs}>No documents available. Upload documents in Admin &gt; Knowledge &gt; Documents.</div>
                ) : (
                  <div style={styles.docList}>
                    {availableDocuments.map(doc => (
                      <div
                        key={doc.id}
                        style={styles.docItem}
                        onClick={() => insertDocumentLink(doc)}
                      >
                        <span style={styles.docIcon}>{getFileIcon(doc.mime_type)}</span>
                        <div style={styles.docInfo}>
                          <div style={styles.docName}>{doc.original_filename}</div>
                          <div style={styles.docMeta}>{doc.category} • {(doc.size_bytes / 1024).toFixed(0)} KB</div>
                        </div>
                        {generatingLink === doc.id ? (
                          <span style={styles.generating}>Generating...</span>
                        ) : (
                          <Link2 size={16} style={{ color: 'var(--brand-primary, #3b82f6)' }} />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div style={styles.footer}>
          <button
            style={{ ...styles.secondaryBtn, opacity: (isLoading || isSending) ? 0.6 : 1 }}
            onClick={loadDraft}
            disabled={isLoading || isSending || isOptedOut}
          >
            <RefreshCw size={16} />
            Regenerate
          </button>

          <button
            style={{ ...styles.primaryBtn, opacity: (!body.trim() || isLoading || isSending || isOptedOut) ? 0.6 : 1 }}
            onClick={handleSend}
            disabled={!body.trim() || isLoading || isSending || isOptedOut || !contact?.phone}
          >
            <Send size={16} />
            {isSending ? 'Sending…' : 'Send'}
          </button>
        </div>

        {isLoading && (
          <div style={styles.loading}>Generating draft…</div>
        )}
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    padding: 16,
  },
  modal: {
    width: '100%',
    maxWidth: 640,
    maxHeight: '90vh',
    backgroundColor: '#0f172a',
    border: '1px solid #334155',
    borderRadius: 14,
    boxShadow: '0 20px 80px rgba(0,0,0,0.45)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    padding: 16,
    borderBottom: '1px solid #1e293b',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 10,
  },
  title: { color: '#f1f5f9', fontSize: 16, fontWeight: 700 },
  subtitle: { color: '#94a3b8', fontSize: 13, marginTop: 4 },
  iconBtn: {
    background: 'transparent',
    border: '1px solid #334155',
    borderRadius: 10,
    color: '#cbd5e1',
    cursor: 'pointer',
    padding: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  warning: {
    padding: '10px 16px',
    background: 'rgba(245, 158, 11, 0.12)',
    borderBottom: '1px solid rgba(245, 158, 11, 0.3)',
    color: '#fde68a',
    fontSize: 13,
  },
  error: {
    padding: '10px 16px',
    background: 'rgba(239, 68, 68, 0.12)',
    borderBottom: '1px solid rgba(239, 68, 68, 0.3)',
    color: '#fecaca',
    fontSize: 13,
  },
  form: { padding: 16, display: 'flex', flexDirection: 'column', gap: 8, overflow: 'auto' },
  label: { color: '#94a3b8', fontSize: 12, fontWeight: 700, marginTop: 8 },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: '#0b1222',
    border: '1px solid #334155',
    borderRadius: 10,
    color: '#e2e8f0',
    fontSize: 14,
    resize: 'vertical',
    fontFamily: 'inherit',
  },
  attachmentsSection: {
    marginTop: 8,
  },
  attachmentsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  attachButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 12px',
    background: 'transparent',
    border: '1px solid var(--brand-primary, #3b82f6)',
    borderRadius: 8,
    color: 'var(--brand-primary, #3b82f6)',
    fontSize: 13,
    cursor: 'pointer',
  },
  documentPicker: {
    border: '1px solid #334155',
    borderRadius: 10,
    background: '#0b1222',
    maxHeight: 200,
    overflow: 'auto',
  },
  loadingDocs: {
    padding: 16,
    textAlign: 'center',
    color: '#94a3b8',
  },
  noDocs: {
    padding: 16,
    textAlign: 'center',
    color: '#94a3b8',
    fontSize: 13,
  },
  docList: {
    padding: 8,
  },
  docItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 10px',
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'background 0.2s',
    ':hover': {
      background: '#1e293b',
    },
  },
  docIcon: {
    fontSize: 20,
  },
  docInfo: {
    flex: 1,
    minWidth: 0,
  },
  docName: {
    color: '#e2e8f0',
    fontSize: 13,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  docMeta: {
    color: '#64748b',
    fontSize: 11,
    marginTop: 2,
  },
  generating: {
    color: 'var(--brand-primary, #3b82f6)',
    fontSize: 12,
  },
  footer: {
    padding: '12px 16px',
    borderTop: '1px solid #1e293b',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
  },
  secondaryBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 14px',
    borderRadius: 10,
    border: '1px solid #334155',
    background: 'transparent',
    color: '#cbd5e1',
    cursor: 'pointer',
  },
  primaryBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 16px',
    borderRadius: 10,
    border: 'none',
    background: 'var(--brand-primary, #3b82f6)',
    color: '#fff',
    fontWeight: 600,
    cursor: 'pointer',
  },
  loading: {
    position: 'absolute',
    inset: 0,
    background: 'rgba(15,23,42,0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#e2e8f0',
    fontSize: 14,
  },
};

export default OutboundSmsComposerModal;
